import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, Wifi, Battery } from 'lucide-react';
import StartMenu from './StartMenu';
import { WindowsLogo } from './Win95Icons';
import { playSound, SOUNDS } from '../App';

// System tray clock component
const SystemClock = () => {
  const [time, setTime] = useState(new Date());
  const [showDate, setShowDate] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('no-NO', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('no-NO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div 
      style={{ position: 'relative' }}
      onMouseEnter={() => setShowDate(true)}
      onMouseLeave={() => setShowDate(false)}
    >
      <div style={{
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
        fontSize: '11px',
        color: '#000',
        padding: '2px 8px',
        cursor: 'default'
      }}>
        {formatTime(time)}
      </div>
      
      {/* Date tooltip */}
      <AnimatePresence>
        {showDate && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: '4px',
              padding: '6px 10px',
              background: '#ffffcc',
              border: '1px solid #000',
              boxShadow: '2px 2px 0 rgba(0,0,0,0.3)',
              fontFamily: "'Segoe UI', Tahoma, sans-serif",
              fontSize: '11px',
              color: '#000',
              whiteSpace: 'nowrap',
              zIndex: 10000
            }}
          >
            {formatDate(time)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// System tray icons
const SystemTray = ({ volume, onVolumeClick }) => {
  const [showVolumeTooltip, setShowVolumeTooltip] = useState(false);
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '0 6px',
      borderLeft: '1px solid #808080',
      marginLeft: '4px'
    }}>
      {/* Volume icon */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={onVolumeClick}
        onMouseEnter={() => setShowVolumeTooltip(true)}
        onMouseLeave={() => setShowVolumeTooltip(false)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {volume > 0 ? (
          <Volume2 size={14} color="#000" />
        ) : (
          <VolumeX size={14} color="#808080" />
        )}
        
        <AnimatePresence>
          {showVolumeTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '4px',
                padding: '4px 8px',
                background: '#ffffcc',
                border: '1px solid #000',
                fontFamily: "'Segoe UI', Tahoma, sans-serif",
                fontSize: '10px',
                color: '#000',
                whiteSpace: 'nowrap',
                zIndex: 10000
              }}
            >
              Volum: {volume}%
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
      
      {/* Network icon */}
      <motion.div
        whileHover={{ scale: 1.1 }}
        style={{ padding: '2px', cursor: 'default' }}
        title="Tilkoblet til nettverk"
      >
        <Wifi size={13} color="#000080" />
      </motion.div>
    </div>
  );
};

export default function Taskbar({
  openWindows,
  minimizedWindows,
  onStartClick,
  onWindowRestore,
  activeWindow,
  crtEnabled,
  setCrtEnabled,
  volume,
  setVolume,
  onBSOD
}) {
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [hoveredWindow, setHoveredWindow] = useState(null);

  const handleStartClick = () => {
    playSound(SOUNDS.click);
    setStartMenuOpen(!startMenuOpen);
    onStartClick?.();
  };

  const handleWindowClick = (windowId) => {
    playSound(SOUNDS.click);
    onWindowRestore?.(windowId);
  };

  // Get display name for window
  const getWindowName = (id) => {
    const names = {
      welcome: 'Velkommen',
      services: 'Tjenester',
      metrics: 'Statistikk',
      about: 'Om Oss',
      contact: 'Kontakt',
      readme: 'Les Meg',
      recycle: 'Papirkurv',
      minesweeper: 'Minesveiper',
      cmd: 'Ledetekst',
      video: 'Demo Video',
      appwizard: 'App-veiviser',
      winamp: 'Winamp',
      mycomputer: 'Min Datamaskin',
      network: 'Nettverk',
      internet: 'Internett'
    };
    return names[id] || id;
  };

  // Get icon for window
  const getWindowIcon = (id) => {
    const icons = {
      welcome: '⭐',
      services: '💼',
      metrics: '📊',
      about: '👥',
      contact: '📧',
      readme: '📄',
      recycle: '🗑️',
      minesweeper: '💣',
      cmd: '💻',
      video: '🎬',
      appwizard: '🧙',
      winamp: '🎵',
      mycomputer: '🖥️',
      network: '🌐',
      internet: '🌍'
    };
    return icons[id] || '📁';
  };

  return (
    <div className="taskbar">
      {/* Start Button */}
      <motion.button
        className={`start-button ${startMenuOpen ? 'active' : ''}`}
        onClick={handleStartClick}
        whileHover={{ filter: 'brightness(1.05)' }}
        whileTap={{ scale: 0.98 }}
        data-testid="start-button"
      >
        <div className="start-button-logo">
          <WindowsLogo size={18} />
        </div>
        <span className="start-button-text">Start</span>
      </motion.button>

      {/* Divider */}
      <div style={{
        width: '2px',
        height: '24px',
        background: 'linear-gradient(180deg, #808080 0%, #fff 50%, #808080 100%)',
        margin: '0 4px'
      }} />

      {/* Open Windows */}
      <div className="taskbar-windows">
        {Object.entries(openWindows).filter(([_, w]) => w.open).map(([id, window]) => (
          <motion.button
            key={id}
            className={`taskbar-window-btn ${activeWindow === id ? 'active' : ''} ${window.minimized ? 'minimized' : ''}`}
            onClick={() => handleWindowClick(id)}
            onMouseEnter={() => setHoveredWindow(id)}
            onMouseLeave={() => setHoveredWindow(null)}
            whileHover={{ y: -1 }}
            whileTap={{ y: 1 }}
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <span className="taskbar-window-icon">{getWindowIcon(id)}</span>
            <span className="taskbar-window-title">{getWindowName(id)}</span>
            
            {/* Active indicator */}
            {activeWindow === id && !window.minimized && (
              <motion.div
                layoutId="activeTaskbarIndicator"
                style={{
                  position: 'absolute',
                  bottom: '2px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '20px',
                  height: '2px',
                  background: '#000080',
                  borderRadius: '1px'
                }}
              />
            )}
          </motion.button>
        ))}
      </div>

      {/* System Tray Area */}
      <div className="taskbar-tray">
        <SystemTray 
          volume={volume} 
          onVolumeClick={() => setVolume(volume > 0 ? 0 : 50)}
        />
        <SystemClock />
      </div>

      {/* Start Menu */}
      <AnimatePresence>
        {startMenuOpen && (
          <StartMenu
            onClose={() => setStartMenuOpen(false)}
            onOpenWindow={(id) => {
              setStartMenuOpen(false);
              onWindowRestore?.(id);
            }}
            onShutdown={onBSOD}
            crtEnabled={crtEnabled}
            setCrtEnabled={setCrtEnabled}
            volume={volume}
            setVolume={setVolume}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
